import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function OwnerCalendarStrip({ blocks }) {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const byDate = useMemo(() => new Map((blocks || []).map((b) => [b.date, b.source])), [blocks]);
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const startDow = new Date(view.y, view.m, 1).getDay();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const iso = (day) => `${view.y}-${String(view.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const move = (dir) =>
    setView((s) => {
      const m = s.m + dir;
      if (m < 0) return { y: s.y - 1, m: 11 };
      if (m > 11) return { y: s.y + 1, m: 0 };
      return { ...s, m };
    });

  return (
    <div data-testid="owner-calendar-strip">
      <div className="flex items-center justify-between">
        <button
          data-testid="owner-cal-prev"
          onClick={() => move(-1)}
          aria-label="Previous month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="font-display text-xl text-cream sm:text-2xl" data-testid="owner-cal-month">
          {MONTHS[view.m]} <span className="text-gold">{view.y}</span>
        </p>
        <button
          data-testid="owner-cal-next"
          onClick={() => move(1)}
          aria-label="Next month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-cream/40">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={`dow-${i}`}>{d}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <span key={`pad-${i}`} />;
          const src = byDate.get(iso(day));
          const cls =
            src === "deposit"
              ? "bg-gold text-espresso font-semibold"
              : src === "manual"
                ? "bg-rose/70 text-cream"
                : "text-cream/70";
          return (
            <span
              key={iso(day)}
              data-testid={src ? `owner-cal-blocked-${iso(day)}` : `owner-cal-day-${iso(day)}`}
              className={`flex h-8 items-center justify-center rounded-md text-xs ${cls}`}
            >
              {day}
            </span>
          );
        })}
      </div>

      <p className="mt-4 flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.12em] text-cream/50">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-gold" /> Paid booking
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-rose/70" /> Owner block
        </span>
      </p>
    </div>
  );
}